import type { ChatMessage, HomeChatCard, ActionChoiceCard } from './types';
import type { Turn } from '../FarChat/types';
import { HomeChatConversationItem } from './HomeChatPanel.types';




/**
 * Return the canonical unified timeline sequence, falling back only for
 * transcripts persisted before the backend started assigning it to every item.
 */
export function timelineSequenceOf(record: unknown): number | null {
  if (!record || typeof record !== 'object') return null;
  const timelineRecord = record as {
    timeline_sequence?: number;
    order?: number;
  };
  if (
    Number.isSafeInteger(timelineRecord.timeline_sequence)
    && timelineRecord.timeline_sequence! >= 0
  ) {
    return timelineRecord.timeline_sequence!;
  }
  if (Number.isSafeInteger(timelineRecord.order) && timelineRecord.order! >= 0) {
    return timelineRecord.order!;
  }
  return null;
}

/**
 * Merge independently stored Home Chat records by the server-owned unified
 * timeline. Pre-timeline records retain the historical render grouping as a
 * backward-compatible fallback, without inventing cross-record ordering.
 */
export function mergeHomeChatConversation(
  messages: ChatMessage[],
  cards: HomeChatCard[],
  turns: Turn[]
): HomeChatConversationItem[] {
  const legacy = messages.flatMap((message, index): HomeChatConversationItem[] =>
    message && typeof message === 'object'
      ? [{
        kind: 'legacy_message',
        timelineSequence: timelineSequenceOf(message),
        index,
        message,
      }]
      : []
  );
  const cardItems = cards.flatMap((card, index): HomeChatConversationItem[] =>
    card && typeof card === 'object'
      ? [{
        kind: 'card',
        timelineSequence: card.timeline_sequence,
        index,
        card,
      }]
      : []
  );
  const turnItems = turns.flatMap((turn, index): HomeChatConversationItem[] =>
    turn && typeof turn === 'object'
      ? [{
        kind: 'turn',
        timelineSequence: timelineSequenceOf(turn),
        index,
        turn,
      }]
      : []
  );
  const kindRank: Record<HomeChatConversationItem['kind'], number> = {
    legacy_message: 0,
    card: 1,
    turn: 2,
  };
  return [...legacy, ...cardItems, ...turnItems].sort((left, right) => {
    // Missing timeline sequence is exclusively a pre-feature fallback. Preserve the old
    // render grouping and stable array position rather than pretending a
    // timestamp or client-side counter is authoritative.
    if (left.timelineSequence === null || right.timelineSequence === null) {
      if (left.timelineSequence !== null) return -1;
      if (right.timelineSequence !== null) return 1;
      return kindRank[left.kind] - kindRank[right.kind] || left.index - right.index;
    }
    return left.timelineSequence - right.timelineSequence || kindRank[left.kind] - kindRank[right.kind] || left.index - right.index;
  });
}

/**
 * The Services branch is a two-step chooser: an initial general action card
 * leads directly to the One-time/Complex Services card. The first card is a
 * durable audit record, but showing it after the successor arrives repeats
 * the user's opening question and looks like two active choice panels.
 */
export function isServicesBranchChoice(card: HomeChatCard): card is ActionChoiceCard {
  return card.kind === 'action_choice'
    && card.state === 'pending'
    && card.options.some((option) => option.label === 'One-time Services')
    && card.options.some((option) => option.label === 'Complex Services');
}

/**
 * A completed in-chat continuation has already produced its own conversation
 * item (a Turn, similar-results card, or controller view). Keeping the
 * routing card's selected receipt in that case looks like a second, stale
 * choice prompt. Workflow-launch receipts stay visible: they document the
 * explicit navigation to Create Workflow.
 *
 * Older persisted workflow choices did not include the navigation hint, so
 * only the reviewed continuation labels are hidden when that hint is absent.
 * That preserves historical workflow receipts without inferring a workflow
 * type or private route from the label.
 */
export const IN_CHAT_ACTION_SELECTION_LABELS = new Set([
  'Get procurement guidance',
  'Find similar past requirements',
  'Search similar requirements',
  'Research products and suppliers — Commodities Market Research',
  'Commodities Market Research',
  'Buy with a purchase card — GPC',
  'Buy with a Government Purchase Card',
  'Start a GPC purchase',
  'Start GPC purchase chat',
  'Start Market Research chat',
  'Generate Construction SOW',
  'Generate Services SOW/PWS',
]);

export function isCompletedInChatRoutingCard(card: HomeChatCard): boolean {
  if (card.kind === 'ambiguous_intent_choice') {
    return card.state === 'selected';
  }
  if (card.kind !== 'action_choice' || card.state !== 'selected') return false;

  const selectedOption = card.options.find(
    (option) => option.label === card.selected_label,
  );
  return selectedOption?.requires_workflow_navigation !== true
    && typeof card.selected_label === 'string'
    && IN_CHAT_ACTION_SELECTION_LABELS.has(card.selected_label);
}

export function visibleHomeChatCards(cards: HomeChatCard[]): HomeChatCard[] {
  const servicesBranchSequences = new Set(
    cards.filter(isServicesBranchChoice).map((card) => card.timeline_sequence),
  );
  return cards.filter((card) => {
    if (isCompletedInChatRoutingCard(card)) return false;
    if (servicesBranchSequences.size === 0) return true;
    const isSelectedInitialChoice = (
      (card.kind === 'action_choice' || card.kind === 'ambiguous_intent_choice')
      && card.state === 'selected'
    );
    // Only hide the immediately preceding selected card. Older decisions in
    // the same conversation remain visible, preserving its audit history.
    return !isSelectedInitialChoice
      || !servicesBranchSequences.has(card.timeline_sequence + 1);
  });
}
